<template>
  <div
    @contextmenu.prevent="contextMenu"
    @dragover.stop.prevent="dragOver"
    @drop.stop.prevent="droppedFiles"
    v-bind:class="{ 'draggingOverNodeView': isDragging }"
    style="min-height: 100%; margin-top: -4px;"
  >
    <NodeToolsBar v-if="!updateInProgress" />

    <UploadFilesDialog
      v-if="getCurrentNode"
      v-model="showUploadDialog"
      :node="getCurrentNode"
      :filesDroppedEvent="filesDroppedEvent"
      @update:modelValue="showUploadDialog = $event"
    />

    <MoveDroppedDialog
      v-if="getCurrentNode"
      v-model="showMoveDroppedDialog"
      :targetNodeId="dropTargetNodeId"
      :podIds="dropPodIds"
      :nodeIds="dropNodeIds"
      @update:modelValue="showMoveDroppedDialog = $event"
    />

    <!-- Overlay to drag and drop files -->
    <v-card
      v-if="!updateInProgress && getCurrentNode && isDragging && !folderError && !rateLimitError"
      elevation="0"
      class="dropFilesOverlay d-flex flex-column align-center justify-center"
    >
      <div>
        <v-icon icon="mdi-image-multiple-outline" :size="64"></v-icon>
      </div>
      <div>
        {{$t("routes.node.dropFilesHere")}}
      </div>
      <v-btn @click="isDragging = false" color="primary" variant="flat" class="mt-8">
        <template v-slot:prepend>
          <v-icon icon="mdi-close"></v-icon>
        </template>
        {{$t('common.close')}}
      </v-btn>
    </v-card>

    <!-- List of nodes and pods -->
    <div v-if="!updateInProgress && getCurrentNode && !folderError && !rateLimitError" v-show="!isDragging" class="files-folders-list">
      <v-row>
        <v-col>
          <InfiniteListView
            v-if="getNodeViewType === 'list' && !updateInProgress"
            v-bind:items="getPagedItems"
            v-bind:isArchived="isArchived"
            :disablePodRequests="disablePodRequests"
            :parentContextMenuStatus="showContextMenu"
            @openedContextMenu="closeCurrentMenu()"
            @resetPaginatedItems="resetShowingItems()"
            @droppedMovableItems="droppedMovableItems($event)"
            class="listing-component"
          >
          </InfiniteListView>

          <InfiniteGridView
            v-if="getNodeViewType === 'grid' && !updateInProgress"
            v-bind:items="getPagedItems"
            :parentContextMenuStatus="showContextMenu"
            @openedContextMenu="closeCurrentMenu()"
            @resetPaginatedItems="resetShowingItems()"
            @droppedMovableItems="droppedMovableItems($event)"
          >
          </InfiniteGridView>
        </v-col>
      </v-row>



      <v-row v-if="!updateInProgress">
        <v-col class="d-flex justify-center">
          <div style="width: fit-content;">
            {{ $t('routes.node.showingNumberOfItems', [getPagedItems.length, getFilteredList.length]) }}

            <v-progress-linear
              v-if="getFilteredList.length !== 0"
              v-show="getPagedItems.length !== getFilteredList.length && isLoadingMore"
              height="4"
              indeterminate
            >
            </v-progress-linear>
          </div>
        </v-col>
      </v-row>

      <!-- Sentinel element for scroll detection -->
      <div ref="scrollSentinel" style="height: 1px;"></div>

      <NodeContextMenu
        v-if="getCurrentNode && getCurrentNode.archived === archiveStatus.NOTARCHIVED"
        v-model="showContextMenu"
        :positionX="contextMenuX"
        :positionY="contextMenuY"
        :node="getCurrentNode"
        :key="contextMenuX * contextMenuY"
      >
      </NodeContextMenu>

      <ArchivedNodeContextMenu
        v-if="getCurrentNode && getCurrentNode.archived === archiveStatus.ARCHIVED"
        v-model="showContextMenu"
        :positionX="contextMenuX"
        :positionY="contextMenuY"
        :node="getCurrentNode"
        :key="contextMenuX * contextMenuY"
      >
      </ArchivedNodeContextMenu>
    </div>

    <!-- Loading indicator -->
    <div v-if="updateInProgress && !getCurrentNode" class="mt-3">
      <v-row>
        <v-col class="text-center">
          <v-progress-circular
            indeterminate
            size="70"
            width="7"
            color="primary"
            :aria-label="$t('routes.node.loadingFolder')"
          ></v-progress-circular>
          <div>{{$t('routes.node.loadingFolder')}}</div>
        </v-col>
      </v-row>
    </div>

    <!-- Folder not found error -->
    <div v-if="!updateInProgress && folderError">
      <v-row>
        <v-col>
          <v-alert type="warning" variant="outlined" class="pa-4">
            <template v-slot:prepend>
              <v-icon size="32" icon="mdi-alert" class="mx-4"></v-icon>
            </template>
            <div class="text-left">
              <h4>{{$t('routes.node.folderNotFound')}}</h4>
            </div>
            <span>{{$t('routes.node.theFolderDoesNotExist')}}</span>
          </v-alert>
        </v-col>
      </v-row>

      <!-- Redirect to the current active access point -->
      <v-row v-if="shouldShowRedirectButton">
        <v-col class="text-center">
          <v-btn
            :to="'/node/'+getActiveAccesspoint.node.id"
            color="primary"
            variant="flat"
            prepend-icon="mdi-home"
          >
            {{$t('routes.node.goTo')}} {{getActiveAccesspoint.node.name}}
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <!-- Rate limit exceeded error -->
    <div v-if="!updateInProgress && rateLimitError">
      <v-row>
        <v-col>
          <v-alert type="warning" variant="outlined" class="pa-4">
            <template v-slot:prepend>
              <v-icon size="32" icon="mdi-clock-alert-outline" class="mx-4"></v-icon>
            </template>
            <div class="text-left">
              <h4>{{ $t('routes.node.rateLimitExceeded') }}</h4>
            </div>
            <span>{{ $t('routes.node.rateLimitExceededDescription') }}</span>
          </v-alert>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="text-center">
          <v-btn
            @click="updateCurrentNode()"
            color="primary"
            variant="flat"
            prepend-icon="mdi-refresh"
          >
            {{ $t('routes.node.retry') }}
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <!-- No permissions to browse folders error -->
    <v-row v-if="!updateInProgress && isInitialized && getDbUser && !this.$canListNodeAndPod()">
      <v-col>
        <v-alert type="error" class="pa-4">
          <template v-slot:prepend>
            <v-icon size="32" icon="mdi-alert" class="mx-4"></v-icon>
          </template>
          <div class="text-left">
            <h4>{{$t('routes.node.accessDenied')}}</h4>
          </div>
          <span>{{$t('routes.node.haveNoPermissions')}}</span>
        </v-alert>
      </v-col>
    </v-row>

  </div>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js'
import { useNodeStore } from '@/stores/NodeStore.js';
import { useCommonStore } from '@/stores/CommonStore.js'
import { useFilterStore } from '@/stores/FilterStore.js'
import _debounce from 'lodash/debounce';
import NodeToolsBar from './components/nodeToolsBar/NodeToolsBar.vue'

import InfiniteGridView from './components/nodeGridView/InfiniteGridView.vue'
import InfiniteListView from './components/nodeListView/InfiniteListView.vue'

import NodeContextMenu from './components/contextMenus/NodeContextMenu.vue'
import ArchivedNodeContextMenu from './components/contextMenus/ArchivedNodeContextMenu.vue'
import UploadFilesDialog from './components/dialogs/UploadFilesDialog.vue'
import Helper from '@/components/helper';
import MoveDroppedDialog from './components/dialogs/MoveDroppedDialog.vue';

export default {
  name: "NodesIndex",
  components: {
    NodeToolsBar,

    InfiniteListView,
    InfiniteGridView,

    NodeContextMenu,
    UploadFilesDialog,
    MoveDroppedDialog,
    ArchivedNodeContextMenu,
  },
  data() {
    return {
      showContextMenu: false,
      contextMenuX: 0,
      contextMenuY: 0,
      isDragging: false,
      lastDragEvent: null,
      folderError: false,
      rateLimitError: false,
      updateInProgress: false,
      disablePodRequests: false,
      itemsPerPage: 20,
      showingItems: 20,
      showUploadDialog: false,
      filesDroppedEvent: null,
      isInitialized: false,
      isLoadingMore: false,
      scrollContainer: null,
      intersectionObserver: null,

      showMoveDroppedDialog: false,
      dropTargetNodeId: null,
      dropPodIds: [],
      dropNodeIds: [],
    };
  },
  mounted() {
    if (window.webkit && window.webkit.messageHandlers) {
      window.initiateFCPUpload = () => {
        this.initiateFCPUpload();
      };
    }

    this.waitForAuthAndInitialize();

    // Setup scroll listener after Vue has rendered
    this.$nextTick(() => {
      this.setupScrollListener();
      this.setupIntersectionObserver();
    });
  },
  beforeUnmount() {
    this.cleanupScrollListener();
    this.cleanupIntersectionObserver();
  },
  watch: {
    '$route.params.id': {
      immediate: false,
      // eslint-disable-next-line no-unused-vars
      async handler(newId, oldId) {
        if (this.isInitialized) {
          await this.updateCurrentNode();
          this.resetShowingItems();
        }
      }
    },

    getDbUser: {
      immediate: true,
      async handler(newUser) {
        if (newUser && !this.isInitialized) {
          await this.initializeData();
        }
      }
    },

    getFilteredList: {
      handler() {
        this.$nextTick(() => {
          this.setupIntersectionObserver();
        });
      }
    }
  },
  methods: {
    setupScrollListener() {
      // Find the v-main element which is the scroll container
      this.scrollContainer = document.querySelector('.v-main__wrap');

      if (!this.scrollContainer) {
        // Fallback to window if v-main not found
        this.scrollContainer = window;
      }

      this.scrollHandler = _debounce(() => {
        const element = this.scrollContainer === window ? document.documentElement : this.scrollContainer;
        const scrollTop = element.scrollTop;
        const scrollHeight = element.scrollHeight;
        const clientHeight = element.clientHeight;

        // Trigger when user is within 200px of the bottom
        if (scrollTop + clientHeight >= scrollHeight - 200) {
          this.loadMoreItems();
        }
      }, 200);

      this.scrollContainer.addEventListener('scroll', this.scrollHandler, { passive: true });
    },

    cleanupScrollListener() {
      if (this.scrollContainer && this.scrollHandler) {
        this.scrollContainer.removeEventListener('scroll', this.scrollHandler);
      }
    },

    setupIntersectionObserver() {
      // Clean up existing observer
      this.cleanupIntersectionObserver();

      // Create intersection observer for the sentinel element
      if (this.$refs.scrollSentinel && window.IntersectionObserver) {
        // Use the scroll container as root if it's not window
        const root = this.scrollContainer === window ? null : this.scrollContainer;

        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this.isLoadingMore && !this.updateInProgress) {
                const hasMoreItems = this.getPagedItems.length < this.getFilteredList.length;
                if (hasMoreItems) {
                  this.loadMoreItems();
                }
              }
            });
          },
          {
            root: root,
            rootMargin: '200px',
            threshold: 0.1
          }
        );

        this.intersectionObserver.observe(this.$refs.scrollSentinel);
      }
    },

    cleanupIntersectionObserver() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
    },

    async waitForAuthAndInitialize() {
      const authStore = useAuthStore();

      if (authStore.dbUser) {
        await this.initializeData();
        return;
      }

      // Wait for auth state (max 5 seconds)
      let attempts = 0;
      const checkAuth = async () => {
        if (authStore.dbUser || attempts++ > 50) {
          await this.initializeData();
          return;
        }
        setTimeout(checkAuth, 100);
      };

      setTimeout(checkAuth, 100);
    },
    async initializeData() {
      if (this.isInitialized) return;

      try {
        if (this.$canListNodeAndPod()) {
          await this.updateCurrentNode();
          this.resetShowingItems();
        }
      } catch (error) {
        console.error('Initialization error:', error);
      } finally {
        this.isInitialized = true;
      }
    },
    async updateCurrentNode() {
      const authStore = useAuthStore();

      if (authStore.sessionInvalidatedDialogVisible || authStore.sessionInvalidatedLogoutPending) {
        return;
      }

      this.folderError = false;
      this.rateLimitError = false;
      this.updateInProgress = true;
      this.disablePodRequests = true;

      try {
        const nodeID = this.$route.params.id;
        if (nodeID) {
          const nodeStore = useNodeStore();
          const fullNodeInfo = await nodeStore.viewNode(nodeID);

          if (fullNodeInfo?.accessPointId) {
            const authStore = useAuthStore();
            authStore._setRootNodeId(fullNodeInfo.accessPointId);
          }

          return fullNodeInfo;
        } else {
          const nodeStore = useNodeStore();
          await nodeStore.viewRoots();
        }
      } catch (error) {
        console.error('Error updating current node:', error);
        if (this.isSessionInvalidatedError(error)) {
          authStore.showSessionInvalidatedLogout();
          return;
        }
        if (error?.response?.status === 429) {
          this.rateLimitError = true;
        } else {
          this.folderError = true;
        }
      } finally {
        this.updateInProgress = false;
        this.disablePodRequests = false;

        // Re-setup observers after content change
        this.$nextTick(() => {
          this.setupIntersectionObserver();
        });
      }
    },
    resetShowingItems() {
      this.showingItems = this.itemsPerPage;
      this.isLoadingMore = false;
      this.$nextTick(() => {
        this.setupIntersectionObserver();
      });
    },
    loadMoreItems() {
      if (this.isLoadingMore || this.showingItems >= this.getFilteredList.length) {
        return;
      }

      this.isLoadingMore = true;

      // Simulate async loading
      setTimeout(() => {
        this.showingItems = Math.min(
          this.showingItems + this.itemsPerPage,
          this.getFilteredList.length
        );
        this.isLoadingMore = false;

        // Re-setup observer after loading more items
        this.$nextTick(() => {
          this.setupIntersectionObserver();
        });
      }, 300);
    },
    initiateFCPUpload() {
      const authStore = useAuthStore();
      authStore.getCurrentSession()
        .then(function(session) {
          if (Helper.isFCPMessageHandler('setCredentials')) {
            const setCredentialsPayload = {
              action: "setCredentials",
              accessKey: session.credentials.accessKeyId,
              secretKey: session.credentials.secretAccessKey,
              sessionToken: session.credentials.sessionToken
            };
            const awsObject = JSON.stringify(setCredentialsPayload);
            Helper.invokeFCPMessageHandler('setCredentials', awsObject);
          }
        });
    },
    contextMenu(event) {
      if (!this.getCurrentNode || this.folderError) {
        return;
      }

      this.showContextMenu = false;
      this.contextMenuX = event.clientX;
      this.contextMenuY = event.clientY;

      this.$nextTick(() => {
        this.showContextMenu = true;
      });
    },
    closeCurrentMenu() {
      // console.log("closeCurrentMenu")
      this.showContextMenu = false;
    },
    isSessionInvalidatedError(error) {
      return error?.response?.data?.error?.code === 1016;
    },
    droppedFiles(event) {
      this.isDragging = false;

      if (!this.getCurrentNode || this.folderError) {
        return;
      }

      if (event &&
        event.dataTransfer &&
        event.dataTransfer.items &&
        event.dataTransfer.items.length &&
        event.dataTransfer.items.length > 0 &&
        event.dataTransfer.items[0].kind === 'file')
      {
        this.filesDroppedEvent = event;
        this.showUploadDialog = true; // Open the dialog
        return;
      }

      if (this.lastDragEvent &&
        this.lastDragEvent.dataTransfer &&
        this.lastDragEvent.dataTransfer.items &&
        this.lastDragEvent.dataTransfer.items.length &&
        this.lastDragEvent.dataTransfer.items.length > 0 &&
        this.lastDragEvent.dataTransfer.items[0].kind === 'file')
      {
        this.filesDroppedEvent = this.lastDragEvent;
        this.showUploadDialog = true; // Open the dialog
        return;
      }
    },
    dragOver(event) {
      if (event &&
        event.dataTransfer &&
        event.dataTransfer.items &&
        event.dataTransfer.items.length &&
        event.dataTransfer.items.length > 0 &&
        event.dataTransfer.items[0].kind === 'file')
      {
        this.isDragging = true;
        this.lastDragEvent = event;
        console.log("Drag event", event, event.dataTransfer.items.length);
      }
    },
    handleDragLeave() {
      this.isDragging = false;
    },

    droppedMovableItems($event) {
      console.log("Dropped items here");
      this.dropTargetNodeId = $event.targetNodeId;
      this.dropPodIds = $event.podIds;
      this.dropNodeIds = $event.nodeIds;

      this.showMoveDroppedDialog = true;
    }
  },
  computed: {
    getDbUser() {
      const authStore = useAuthStore();
      return authStore.dbUser;
    },
    archiveStatus() {
      const commonStore = useCommonStore();
      return commonStore.archiveStatus;
    },
    getCurrentNode: function() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNode;
    },
    getNodeViewType: function() {
      const nodeStore = useNodeStore();
      return nodeStore.getNodeView;
    },
    getPagedItems: function() {
      return this.getFilteredList.slice(0, this.showingItems);
    },
    getFilteredList: function() {
      const filterStore = useFilterStore();
      return filterStore.getFilteredList;
    },
    isArchived() {
      return this.getCurrentNode.isArchived > 0;
    },
    getActiveAccesspoint() {
      const authStore = useAuthStore();
      return authStore.getActiveAccesspoint;
    },
    shouldShowRedirectButton() {
      if (this.getActiveAccesspoint) {
        const newRoute = '/node/' + this.getActiveAccesspoint.node.id;

        if (this.$route.path !== newRoute) {
          return true;
        }
      }

      return false;
    },

  }
}
</script>

<style>
.draggingOverNodeView {
  /* background-color: #3dabff !important; */
}

.dropFilesOverlay {
  /* background-color: red; */
  height: 100vh;
  border: 6px dashed #3dabff !important;
  opacity: 0.7;
}
</style>
